import React, { useState, useMemo, useEffect, useRef } from 'react';
import Chart from 'react-apexcharts';
import { Calendar, FilterX, ChevronLeft, ChevronRight, Trash2, Layout, Activity, Maximize2, Minimize2, ArrowsUpFromLine, LineChart, AreaChart, BarChart2, Info, X, Box, Hash, Plus, Pencil, Flag, Download, AlertTriangle, Radio, Pause, Play, RotateCcw, Circle, Square, History } from 'lucide-react';
import { ApexOptions } from 'apexcharts';
import IfcViewerModal from './IfcViewerModal';
import RecordingsModal from './RecordingsModal';
import api from '../api/axios';
import { downloadReadingsAsCsv } from '../utils/csv';

export interface Reading {
  date: string;
  [key: string]: any;
}

interface Dataset {
  id: string;
  label: string;
  data: Reading[];
  metadata?: {
    unit?: string;
    sensorType?: string;
    defaultChartType?: string;
    supportedChartTypes?: string[];
    selectedColumns?: string[];
    description?: string;
    ifcId?: string;
    dataInstalacao?: string;
    imageUrl?: string;
    imageName?: string;
    dataSource?: string;
    // Limiares de alerta por eixo/coluna (opcionais): uma leitura desse canal fora do
    // intervalo definido para ele conta como excedência. Substitui o antigo par único
    // limiteInferior/limiteSuperior (que se aplicava a todas as colunas por igual - não
    // fazia sentido para sensores multi-eixo como um acelerómetro X/Y/Z).
    limites?: Record<string, { limiteInferior?: number | null; limiteSuperior?: number | null }>;
    // Sensor em tempo real: em vez de "data" guardado no projeto, a dashboard faz
    // polling periódico a GET /projects/{id}/datasets/{id}/realtime no auth-service
    // (que por sua vez fala com o mqtt-handler em "dataSource" - o browser nunca lhe
    // fala diretamente, ver a nota sobre o proxy de tempo real em CLAUDE.md) e mostra
    // as leituras ao vivo. Ver mqtt-handler/wilow_realtime_bridge.py e o modo "API" do
    // AddDataResourceModal.tsx.
    sourceType?: string;
    pollIntervalMs?: number;
  };
}

// Persistência do buffer/pausa de um sensor em tempo real: só no sessionStorage do
// browser (nunca no projeto/backend) — sobrevive a um F5 mas não a fechar o separador,
// o que é suficiente para não perder o que se estava a ver por engano.
const REALTIME_BUFFER_KEY = (datasetId: string) => `ph_realtime_buffer_${datasetId}`;
const REALTIME_PAUSED_KEY = (datasetId: string) => `ph_realtime_paused_${datasetId}`;

const loadStoredBuffer = (datasetId: string): Reading[] => {
  try {
    const raw = sessionStorage.getItem(REALTIME_BUFFER_KEY(datasetId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const loadStoredPaused = (datasetId: string): boolean => {
  try {
    return sessionStorage.getItem(REALTIME_PAUSED_KEY(datasetId)) === '1';
  } catch {
    return false;
  }
};

// Evento do projeto marcado na linha temporal dos gráficos.
interface ProjectEvent {
  id: string;
  titulo: string;
  data: string;
  tipo?: 'campanha' | 'intervencao' | 'outro';
}

export const EVENT_TYPES: { key: string; label: string; color: string }[] = [
  { key: 'campanha', label: 'Campanha', color: '#3b82f6' },
  { key: 'intervencao', label: 'Intervenção', color: '#f59e0b' },
  { key: 'outro', label: 'Outro', color: '#64748b' },
];

export const eventColor = (tipo?: string) => EVENT_TYPES.find(t => t.key === tipo)?.color || '#64748b';

// Cor de cada série do gráfico principal, por posição na lista de colunas - reutilizada
// nas linhas de limiar e nos pontos em excedência para corresponder à cor do próprio eixo.
const SERIES_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

// Anotações verticais do ApexCharts para os eventos do projeto (partilhado com a visualização conjunta).
export const eventAnnotations = (eventos: ProjectEvent[], isDarkMode: boolean) =>
  (eventos || []).map(ev => ({
    x: new Date(ev.data).getTime(),
    strokeDashArray: 4,
    borderColor: eventColor(ev.tipo),
    label: {
      text: ev.titulo,
      borderColor: 'transparent',
      orientation: 'horizontal',
      style: {
        color: '#fff',
        background: eventColor(ev.tipo),
        fontSize: '10px',
        fontWeight: 700,
        padding: { left: 6, right: 6, top: 2, bottom: 2 },
      },
    },
  }));

// Gravação persistida de um sensor em tempo real, por uma duração escolhida pelo utilizador.
export interface RecordingFftChannel {
  frequencies: number[];
  magnitudes: number[];
}

export interface RecordingFft {
  sampling_freq: number;
  channels: Record<string, RecordingFftChannel>;
}

export interface Recording {
  id: string;
  titulo: string;
  sensorLabel?: string;
  sensorId?: string;
  unit?: string;
  selectedColumns?: string[];
  startedAt?: string;
  finishedAt?: string;
  requestedDurationSec?: number;
  tipo?: 'manual' | 'alerta';
  triggerColumn?: string;
  triggerValue?: number;
  triggerThreshold?: { limiteInferior?: number | null; limiteSuperior?: number | null };
  fft?: RecordingFft;
  // As leituras já não vêm embutidas em GET /projects/{id} (guardadas à parte numa
  // hypertable do TimescaleDB - ver auth-service/main.py) - "n_readings" chega para a
  // contagem; o RecordingsModal busca "data" à parte (GET .../recordings/{id}/data)
  // só quando a gravação é realmente aberta/mostrada.
  n_readings?: number;
  data?: Reading[];
}

interface MonitoringDashboardProps {
  datasets: Dataset[];
  onDeleteDataset: (id: string) => void;
  canDelete: boolean;
  onAppendData?: (dataset: Dataset) => void;
  projectId?: string;
  hasIfc?: boolean;
  onDatasetUpdated?: () => void;
  eventos?: ProjectEvent[];
  gravacoes?: Recording[];
}

const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({ datasets, onDeleteDataset, canDelete, onAppendData, projectId, hasIfc, onDatasetUpdated, eventos = [], gravacoes = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewMode, setViewMode] = useState<any>('area');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartHeight, setChartHeight] = useState(350);
  const [showProfile, setShowProfile] = useState(false);
  const [showObjectViewer, setShowObjectViewer] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<any>({ label: '', description: '', ifcId: '', dataInstalacao: '', limites: {} as Record<string, { limiteInferior: string; limiteSuperior: string }> });
  const [showExceeding, setShowExceeding] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [eventForm, setEventForm] = useState<any>({ titulo: '', data: '', tipo: 'campanha' });
  const [savingEvent, setSavingEvent] = useState(false);

  // --- Sensores em tempo real: buffer por sensor, hidratado do sessionStorage no
  // 1.º render (sobrevive a um refresh da página; nunca é guardado no projeto) ---
  const [realtimeBuffers, setRealtimeBuffers] = useState<{ [datasetId: string]: Reading[] }>(() => {
    const initial: { [datasetId: string]: Reading[] } = {};
    (datasets || []).forEach(ds => {
      if (ds.metadata?.sourceType === 'realtime') initial[ds.id] = loadStoredBuffer(ds.id);
    });
    return initial;
  });
  // Histórico persistido (até 31 dias, ver realtime_readings/TIMESCALE_MIGRATIONS em
  // database.py) - ao contrário de "realtimeBuffers" (só o que chegou por polling desde
  // que esta aba foi aberta), isto vem do backend uma única vez por sensor/sessão, já
  // agregado (ver GET .../realtime-history), para o gráfico não abrir vazio sempre que
  // se volta a um sensor.
  const [realtimeHistory, setRealtimeHistory] = useState<{ [datasetId: string]: Reading[] }>({});
  const [realtimeHistoryLoaded, setRealtimeHistoryLoaded] = useState<{ [datasetId: string]: boolean }>({});
  const [realtimePollSec, setRealtimePollSec] = useState(0.5);
  const [realtimeError, setRealtimeError] = useState('');
  const [realtimePaused, setRealtimePaused] = useState(() => (
    datasets && datasets[0] ? loadStoredPaused(datasets[0].id) : false
  ));
  const REALTIME_BUFFER_MAX = 400; // pontos guardados por sensor (janela deslizante)

  // Zoom/pan manual do utilizador no gráfico. Tem de ser ESTADO (não ref): o botão de
  // repor zoom do próprio gráfico só limpa isto, e se não houver mais nenhum motivo para
  // voltar a desenhar (ex.: sensor em pausa, ou dataset estático), uma ref nunca chegaria
  // a refletir-se no gráfico - com estado, a limpeza força logo o novo render.
  const [zoomRange, setZoomRange] = useState<{ min: number; max: number } | null>(null);
  // Ver o comentário junto a "beforeResetZoom" abaixo: o próprio ApexCharts chama "zoomed"
  // como parte do reset nativo - esta flag ignora essa única chamada seguinte.
  const suppressNextZoomRef = useRef(false);

  // --- Gravação por duração: snapshot de um sensor em tempo real, persistido no projeto ---
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [recordDuration, setRecordDuration] = useState(60);
  const [recordUnit, setRecordUnit] = useState<'segundos' | 'minutos' | 'horas'>('segundos');
  const [recording, setRecording] = useState<{
    datasetId: string; buffer: Reading[]; startedAt: number; deadline: number; totalSec: number;
  } | null>(null);
  const [recordRemainingSec, setRecordRemainingSec] = useState(0);
  const [savingRecording, setSavingRecording] = useState(false);
  const [showRecordings, setShowRecordings] = useState(false);

  const recordUnitToSec = (v: number, unit: string) => v * (unit === 'horas' ? 3600 : unit === 'minutos' ? 60 : 1);

  // Ref sincronizada com "recording": o polling (tick) e o temporizador da gravação
  // correm dentro de closures que não veem estado atualizado sem isto - sem a ref,
  // "guardar agora"/"parar ao fim do tempo" gravariam sempre um buffer vazio/antigo.
  const recordingRef = useRef(recording);
  useEffect(() => { recordingRef.current = recording; }, [recording]);

  const startRecording = () => {
    if (!currentDataset || !isRealtime) return;
    const totalSec = Math.min(24 * 3600, Math.max(1, Math.round(recordUnitToSec(recordDuration, recordUnit))));
    const now = Date.now();
    setRecording({ datasetId: currentDataset.id, buffer: [], startedAt: now, deadline: now + totalSec * 1000, totalSec });
    setShowRecordForm(false);
  };

  const finishRecording = async (save: boolean) => {
    const rec = recordingRef.current;
    setRecording(null);
    setRecordRemainingSec(0);
    if (!save || !rec || rec.buffer.length === 0 || !projectId) return;
    setSavingRecording(true);
    try {
      await api.post(`/projects/${projectId}/recordings`, {
        titulo: `${label} · ${new Date(rec.startedAt).toLocaleString('pt-PT')}`,
        sensorLabel: label,
        sensorId: rec.datasetId,
        unit,
        selectedColumns: columnKeys,
        startedAt: new Date(rec.startedAt).toISOString(),
        finishedAt: new Date().toISOString(),
        requestedDurationSec: rec.totalSec,
        data: rec.buffer,
      });
      onDatasetUpdated && onDatasetUpdated();
    } finally {
      setSavingRecording(false);
    }
  };

  // Termina (e guarda) automaticamente quando o tempo pedido se esgota. Depende só do
  // "deadline" (fixo durante toda a gravação) - não reinicia o temporizador a cada
  // amostra recebida, o que aconteceria se dependesse do objeto "recording" completo.
  useEffect(() => {
    if (!recording) { setRecordRemainingSec(0); return; }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((recording.deadline - Date.now()) / 1000));
      setRecordRemainingSec(remaining);
      if (remaining <= 0) finishRecording(true);
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording?.deadline]);

  const chartTypeMap: { [key: string]: string } = {
    'Linha': 'line',
    'Área': 'area',
    'Barras': 'bar',
    'Scatter': 'scatter'
  };

  const currentDataset = datasets && datasets.length > 0 ? datasets[currentIndex] : null;
  const isRealtime = currentDataset?.metadata?.sourceType === 'realtime';
  const data = currentDataset ? (isRealtime
    ? [...(realtimeHistory[currentDataset.id] || []), ...(realtimeBuffers[currentDataset.id] || [])]
    : currentDataset.data) : [];
  const label = currentDataset ? currentDataset.label : 'Sem dados';
  const unit = currentDataset?.metadata?.unit || 'ºC';

  const columnKeys = useMemo(() => {
    if (!currentDataset) return [];
    return currentDataset.metadata?.selectedColumns
      || (data?.[0] ? Object.keys(data[0]).filter(k => k !== 'date') : []);
  }, [currentDataset, data]);

  // Ao mudar de sensor, alinha o intervalo de leitura com o guardado no perfil e
  // recupera a pausa/buffer guardados (por sensor) - útil tanto ao trocar de sensor
  // como já foi restaurado ao abrir a página.
  useEffect(() => {
    if (currentDataset?.metadata?.sourceType === 'realtime') {
      setRealtimePollSec((currentDataset.metadata.pollIntervalMs || 500) / 1000);
      setRealtimeError('');
      setRealtimePaused(loadStoredPaused(currentDataset.id));
      setRealtimeBuffers(prev => (
        prev[currentDataset.id] ? prev : { ...prev, [currentDataset.id]: loadStoredBuffer(currentDataset.id) }
      ));
    }
    setZoomRange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDataset?.id]);

  // Um filtro de datas explícito também deve substituir qualquer zoom manual anterior.
  useEffect(() => {
    setZoomRange(null);
  }, [startDate, endDate]);

  // Grava o estado da pausa e o buffer no sessionStorage, para sobreviverem a um refresh.
  useEffect(() => {
    if (!currentDataset || !isRealtime) return;
    try { sessionStorage.setItem(REALTIME_PAUSED_KEY(currentDataset.id), realtimePaused ? '1' : '0'); } catch {}
  }, [realtimePaused, currentDataset?.id, isRealtime]);

  useEffect(() => {
    if (!currentDataset || !isRealtime) return;
    const buf = realtimeBuffers[currentDataset.id];
    if (!buf) return;
    try { sessionStorage.setItem(REALTIME_BUFFER_KEY(currentDataset.id), JSON.stringify(buf)); } catch {}
  }, [realtimeBuffers, currentDataset?.id, isRealtime]);

  // Carrega o histórico persistido (até 31 dias) uma única vez por sensor/sessão, ao
  // abri-lo - sem isto o gráfico só mostraria o que chegasse por polling a partir de
  // agora, dando a impressão errada de que nada tinha sido guardado enquanto a aba
  // esteve fechada (os dados já estavam a ser gravados na mesma, só não eram mostrados).
  useEffect(() => {
    if (!isRealtime || !currentDataset || !projectId) return;
    const datasetId = currentDataset.id;
    if (realtimeHistoryLoaded[datasetId]) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/projects/${projectId}/datasets/${datasetId}/realtime-history`);
        if (cancelled) return;
        setRealtimeHistory(prev => ({ ...prev, [datasetId]: res.data?.data || [] }));
      } catch {
        // Falha a carregar histórico não impede o polling ao vivo de continuar.
      } finally {
        if (!cancelled) setRealtimeHistoryLoaded(prev => ({ ...prev, [datasetId]: true }));
      }
    })();
    return () => { cancelled = true; };
  }, [isRealtime, currentDataset?.id, projectId, realtimeHistoryLoaded]);

  // Quando o utilizador escolhe um intervalo de datas para um sensor em tempo real (os
  // mesmos campos "startDate"/"endDate" usados para filtrar sensores estáticos), vai
  // buscar o histórico de novo, mas desta vez só para esse intervalo - sem isto, o
  // orçamento de pontos (ver realtime-history no backend) ficava sempre espalhado pelos
  // últimos 31 dias inteiros, dando pouquíssima resolução (ex.: ~270 pontos) para quem só
  // queria ver um dia específico.
  useEffect(() => {
    if (!isRealtime || !currentDataset || !projectId) return;
    if (!startDate && !endDate) return;
    const datasetId = currentDataset.id;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (startDate) params.set('start', startDate);
        params.set('end', endDate || startDate);
        const res = await api.get(`/projects/${projectId}/datasets/${datasetId}/realtime-history?${params.toString()}`);
        if (cancelled) return;
        setRealtimeHistory(prev => ({ ...prev, [datasetId]: res.data?.data || [] }));
      } catch {
        // Mantém o histórico anterior se o pedido falhar.
      }
    })();
    return () => { cancelled = true; };
  }, [isRealtime, currentDataset?.id, projectId, startDate, endDate]);

  // Polling do sensor em tempo real: a cada "realtimePollSec" segundos, pede a leitura
  // mais recente ao auth-service (que por sua vez fala com o mqtt-handler, guardado em
  // metadata.dataSource) e acrescenta-a a um buffer local (não é guardado no projeto -
  // é só para visualização ao vivo). Passa sempre pelo auth-service (autenticado, com as
  // permissões de projeto normais) em vez de o browser falar HTTP diretamente com o
  // mqtt-handler - ver a nota em CLAUDE.md sobre o proxy de tempo real.
  useEffect(() => {
    if (!isRealtime || !currentDataset || !projectId || realtimePaused) return;

    const datasetId = currentDataset.id;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/datasets/${datasetId}/realtime`);
        const body = res.data;
        if (cancelled) return;
        setRealtimeError('');

        // O backend pode devolver uma pequena janela recente (body.history, últimos
        // segundos em RAM) além da leitura mais recente - usa-se para o gráfico não
        // abrir vazio e para não perder amostras entre polls; sem "history", cai para
        // só a leitura mais recente (compatível com um bridge mais antigo).
        const rawHistory = Array.isArray(body.history) && body.history.length > 0
          ? body.history
          : [{ timestamp: body.timestamp, channels: body.channels }];

        setRealtimeBuffers(prev => {
          const existing = prev[datasetId] || [];
          const lastTs = existing.length ? new Date(existing[existing.length - 1].date).getTime() : -Infinity;
          const newEntries: Reading[] = rawHistory
            .filter((h: any) => typeof h.timestamp === 'number' && h.timestamp * 1000 > lastTs)
            .map((h: any) => ({ date: new Date(h.timestamp * 1000).toISOString(), ...(h.channels || {}) }));
          if (newEntries.length === 0) return prev;
          const arr = [...existing, ...newEntries];
          return { ...prev, [datasetId]: arr.length > REALTIME_BUFFER_MAX ? arr.slice(arr.length - REALTIME_BUFFER_MAX) : arr };
        });

        // Se houver uma gravação ativa para este sensor, acrescenta as mesmas leituras
        // novas ao seu buffer (só em memória até se guardar no fim/ao parar). O "history"
        // do backend cobre os últimos ~10s ANTES deste poll — na 1.ª leitura da gravação
        // isso incluiria amostras de antes de teres clicado em gravar, por isso o corte
        // nunca pode ser anterior a "startedAt", mesmo com o buffer da gravação vazio.
        if (recordingRef.current && recordingRef.current.datasetId === datasetId) {
          const rec = recordingRef.current;
          const recLastTs = rec.buffer.length
            ? new Date(rec.buffer[rec.buffer.length - 1].date).getTime()
            : rec.startedAt - 1;
          const recNewEntries: Reading[] = rawHistory
            .filter((h: any) => typeof h.timestamp === 'number' && h.timestamp * 1000 > recLastTs)
            .map((h: any) => ({ date: new Date(h.timestamp * 1000).toISOString(), ...(h.channels || {}) }));
          if (recNewEntries.length > 0) {
            setRecording(prev => (prev ? { ...prev, buffer: [...prev.buffer, ...recNewEntries] } : prev));
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        const apiError = err?.response?.data?.error;
        setRealtimeError(apiError || 'Sem ligação ao sensor — confirma que o URL está acessível e que a ponte MQTT está a correr.');
      }
    };

    tick();
    const intervalId = setInterval(tick, Math.max(100, realtimePollSec * 1000));
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [isRealtime, currentDataset?.id, projectId, realtimePollSec, realtimePaused]);

  useMemo(() => {
    if (currentDataset?.metadata?.defaultChartType) {
      setViewMode(chartTypeMap[currentDataset.metadata.defaultChartType] || 'area');
    }
  }, [currentDataset]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter(item => {
      const itemDate = item.date.slice(0, 10);
      if (startDate && itemDate < startDate) return false;
      if (endDate && itemDate > endDate) return false;
      return true;
    });
  }, [data, startDate, endDate]);

  const series = useMemo(() => {
    if (!currentDataset || !filteredData.length) return [];
    
    // Use metadata selectedColumns if available
    const keys = currentDataset.metadata?.selectedColumns || Object.keys(filteredData[0]).filter(k => k !== 'date');
    
    return keys.map((key: string) => ({
      name: key,
      data: filteredData.map(d => ({ x: new Date(d.date).getTime(), y: d[key] }))
    }));
  }, [currentDataset, filteredData]);

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    // Sem isto, limpar o intervalo de um sensor em tempo real deixava o histórico com a
    // última janela escolhida (ex.: só 1 dia de há uma semana) em vez de voltar à visão
    // geral dos últimos 31 dias.
    if (isRealtime && currentDataset) {
      setRealtimeHistoryLoaded(prev => ({ ...prev, [currentDataset.id]: false }));
    }
  };

  const shiftHistoryDays = (delta: number) => {
    const addDays = (d: string, n: number) => {
      const dt = new Date(d + 'T00:00:00Z');
      dt.setUTCDate(dt.getUTCDate() + n);
      return dt.toISOString().slice(0, 10);
    };
    const base = startDate || endDate || new Date().toISOString().slice(0, 10);
    if (startDate && endDate) {
      setStartDate(addDays(startDate, delta));
      setEndDate(addDays(endDate, delta));
    } else {
      const day = addDays(base, delta);
      setStartDate(day);
      setEndDate(day);
    }
  };

  const nextDataset = () => {
    setCurrentIndex((prev) => (prev + 1) % datasets.length);
    clearFilters();
  };

  const prevDataset = () => {
    setCurrentIndex((prev) => (prev - 1 + datasets.length) % datasets.length);
    clearFilters();
  };

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const adjustHeight = () => {
    setChartHeight(prev => prev === 500 ? 800 : prev === 800 ? 350 : 500);
  };

  const minMax = useMemo(() => {
    if (!series.length || !series[0].data.length) return { min: undefined, max: undefined };
    let min = series[0].data[0].x;
    let max = series[0].data[0].x;
    series.forEach(s => {
      s.data.forEach((d: any) => {
        if (d.x < min) min = d.x;
        if (d.x > max) max = d.x;
      });
    });
    return { min, max };
  }, [series]);

  const closeProfile = () => { setShowProfile(false); setEditingProfile(false); };

  const openEditProfile = () => {
    const initialLimites: Record<string, { limiteInferior: string; limiteSuperior: string }> = {};
    columnKeys.forEach(col => {
      const lim = currentDataset?.metadata?.limites?.[col];
      initialLimites[col] = {
        limiteInferior: lim?.limiteInferior != null ? String(lim.limiteInferior) : '',
        limiteSuperior: lim?.limiteSuperior != null ? String(lim.limiteSuperior) : '',
      };
    });
    setProfileForm({
      label: currentDataset?.label || '',
      description: currentDataset?.metadata?.description || '',
      ifcId: currentDataset?.metadata?.ifcId || '',
      dataInstalacao: currentDataset?.metadata?.dataInstalacao || '',
      limites: initialLimites,
    });
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    if (!projectId || !currentDataset) return;
    setSavingProfile(true);
    try {
      const parseLimit = (v: any) => {
        const n = parseFloat(String(v).replace(',', '.'));
        return isNaN(n) ? null : n;
      };
      const newLimites: Record<string, { limiteInferior: number | null; limiteSuperior: number | null }> = {};
      columnKeys.forEach(col => {
        const lo = parseLimit(profileForm.limites?.[col]?.limiteInferior);
        const hi = parseLimit(profileForm.limites?.[col]?.limiteSuperior);
        if (lo != null || hi != null) newLimites[col] = { limiteInferior: lo, limiteSuperior: hi };
      });
      await api.patch(`/projects/${projectId}/datasets/${currentDataset.id}`, {
        label: profileForm.label,
        metadata: {
          description: profileForm.description,
          ifcId: profileForm.ifcId,
          dataInstalacao: profileForm.dataInstalacao || null,
          limites: newLimites,
          // Migração: limpa o antigo par único (substituído pelos limiares por eixo acima).
          limiteInferior: null,
          limiteSuperior: null,
        },
      });
      setEditingProfile(false);
      onDatasetUpdated && onDatasetUpdated();
    } finally {
      setSavingProfile(false);
    }
  };

  const addEvent = async () => {
    if (!projectId || !eventForm.titulo.trim() || !eventForm.data) return;
    setSavingEvent(true);
    try {
      await api.post(`/projects/${projectId}/events`, eventForm);
      setEventForm({ titulo: '', data: '', tipo: 'campanha' });
      onDatasetUpdated && onDatasetUpdated();
    } finally {
      setSavingEvent(false);
    }
  };

  const deleteEvent = async (eventId: string) => {
    if (!projectId) return;
    await api.delete(`/projects/${projectId}/events/${eventId}`);
    onDatasetUpdated && onDatasetUpdated();
  };

  // --- Limiares de alerta (um par mín/máx por eixo/coluna) e estatísticas do intervalo filtrado ---
  const limites: Record<string, { limiteInferior?: number | null; limiteSuperior?: number | null }> =
    currentDataset?.metadata?.limites || {};
  const hasLimits = columnKeys.some(col => {
    const l = limites[col];
    return !!l && (l.limiteInferior != null || l.limiteSuperior != null);
  });

  const stats = useMemo(() => {
    return columnKeys.map(col => {
      const lim = limites[col];
      const colInferior = lim?.limiteInferior ?? null;
      const colSuperior = lim?.limiteSuperior ?? null;
      const hasLimit = colInferior != null || colSuperior != null;
      let min = Infinity, max = -Infinity, sum = 0, n = 0, last: number | null = null, exceed = 0;
      filteredData.forEach(row => {
        const v = row[col];
        if (typeof v !== 'number' || isNaN(v)) return;
        if (v < min) min = v;
        if (v > max) max = v;
        sum += v; n += 1; last = v;
        if ((colSuperior != null && v > colSuperior) || (colInferior != null && v < colInferior)) exceed += 1;
      });
      return n === 0
        ? { col, n, min: null, max: null, media: null, last: null, exceed: 0, hasLimit, limiteInferior: colInferior, limiteSuperior: colSuperior }
        : { col, n, min, max, media: sum / n, last, exceed, hasLimit, limiteInferior: colInferior, limiteSuperior: colSuperior };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKeys, filteredData, currentDataset?.metadata?.limites]);

  // Leituras que passam o limiar do respetivo eixo - guarda o valor concreto (não só a
  // contagem) para se poderem mostrar/listar, e para desenhar os pontos no gráfico.
  const exceedingReadings = useMemo(() => {
    if (!hasLimits) return [];
    const out: { date: string; col: string; value: number; limiteInferior: number | null; limiteSuperior: number | null }[] = [];
    filteredData.forEach(row => {
      columnKeys.forEach(col => {
        const v = row[col];
        if (typeof v !== 'number' || isNaN(v)) return;
        const lim = limites[col];
        if (!lim) return;
        const lo = lim.limiteInferior ?? null;
        const hi = lim.limiteSuperior ?? null;
        if ((hi != null && v > hi) || (lo != null && v < lo)) {
          out.push({ date: row.date, col, value: v, limiteInferior: lo, limiteSuperior: hi });
        }
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, columnKeys, currentDataset?.metadata?.limites, hasLimits]);

  // Nº de leituras (não de excedências por canal) com pelo menos um canal fora do seu
  // limiar - várias excedências na mesma leitura (ex.: X e Z ao mesmo tempo) contam
  // uma só vez, para nunca passar acima do nº total de leituras.
  const totalExceed = useMemo(
    () => new Set(exceedingReadings.map(r => r.date)).size,
    [exceedingReadings]
  );

  // Gravações associadas só a este sensor (não as do projeto todo) - cada sensor em
  // tempo real tem as suas próprias, faz mais sentido ver "as gravações deste sensor"
  // do que uma lista única misturada de todos os sensores do projeto.
  const sensorRecordings = useMemo(
    () => gravacoes.filter(g => g.sensorId === currentDataset?.id),
    [gravacoes, currentDataset?.id]
  );

  // Exporta o intervalo filtrado para CSV (separador ';' e BOM, amigável para o Excel PT).
  const exportCsv = () => {
    if (!currentDataset || !filteredData.length) return;
    const range = `${startDate || 'inicio'}_${endDate || 'fim'}`;
    downloadReadingsAsCsv(filteredData, columnKeys, `${label}_${range}`);
  };

  if (!datasets || datasets.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 p-12 rounded-3xl border border-slate-200 dark:border-slate-700 text-center shadow-sm">
        <Activity className="mx-auto text-slate-300 dark:text-slate-600 mb-3" size={36} />
        <p className="text-slate-500 dark:text-slate-400 font-medium">Sem dados de monitorização.</p>
        {canDelete && <p className="text-sm text-slate-400 mt-1">Adiciona um sensor para começares.</p>}
      </div>
    );
  }

  const isDarkMode = document.documentElement.classList.contains('dark');

  const options: ApexOptions = {
    chart: {
      id: 'main-chart',
      type: viewMode,
      toolbar: { show: true, autoSelected: 'zoom', tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
      animations: { enabled: false },
      background: 'transparent',
      foreColor: isDarkMode ? '#94a3b8' : '#64748b',
      // Guarda o zoom/pan manual num estado para sobreviver a re-renders (novas leituras
      // em tempo real não devem "puxar" o eixo de volta ao intervalo completo dos dados).
      // O próprio botão nativo de repor zoom (Toolbar.handleZoomReset, no código-fonte do
      // apexcharts) chama beforeResetZoom E DEPOIS volta a chamar "zoomed" com o intervalo
      // reposto — sem a supressão abaixo, esse "zoomed" reescrevia logo o zoomRange que
      // acabávamos de limpar, e o botão parecia não fazer nada.
      events: {
        zoomed: (_ctx: any, { xaxis }: any) => {
          if (suppressNextZoomRef.current) { suppressNextZoomRef.current = false; return; }
          setZoomRange({ min: xaxis.min, max: xaxis.max });
        },
        scrolled: (_ctx: any, { xaxis }: any) => setZoomRange({ min: xaxis.min, max: xaxis.max }),
        beforeResetZoom: () => { suppressNextZoomRef.current = true; setZoomRange(null); },
      },
    },
    dataLabels: { enabled: false },
    // Linhas de limiar (horizontais, uma por eixo com limiar definido, na cor da sua
    // série), eventos do projeto (verticais) e pontos em excedência sobre o gráfico.
    annotations: {
      yaxis: columnKeys.flatMap((col, idx) => {
        const lim = limites[col];
        if (!lim) return [];
        const color = SERIES_COLORS[idx % SERIES_COLORS.length];
        const lines: any[] = [];
        if (lim.limiteSuperior != null) {
          lines.push({
            y: lim.limiteSuperior,
            borderColor: color,
            strokeDashArray: 6,
            label: { text: `${col} · máx ${lim.limiteSuperior} ${unit}`, position: 'left', textAnchor: 'start', borderColor: 'transparent', style: { color: '#fff', background: color, fontSize: '10px', fontWeight: 700 } },
          });
        }
        if (lim.limiteInferior != null) {
          lines.push({
            y: lim.limiteInferior,
            borderColor: color,
            strokeDashArray: 6,
            label: { text: `${col} · mín ${lim.limiteInferior} ${unit}`, position: 'left', textAnchor: 'start', borderColor: 'transparent', style: { color: '#fff', background: color, fontSize: '10px', fontWeight: 700 } },
          });
        }
        return lines;
      }),
      points: exceedingReadings.map(r => ({
        x: new Date(r.date).getTime(),
        y: r.value,
        marker: { size: 4, fillColor: '#ef4444', strokeColor: isDarkMode ? '#0b0f1a' : '#fff', strokeWidth: 2 },
      })) as any,
      xaxis: eventAnnotations(eventos, isDarkMode),
    },
    stroke: { curve: 'smooth', width: viewMode === 'bar' ? 0 : 2.5, lineCap: 'round' },
    fill: viewMode === 'area'
      ? {
          type: 'gradient',
          gradient: { shadeIntensity: 0, opacityFrom: 0.18, opacityTo: 0.02, stops: [0, 100] },
        }
      : { type: 'solid', opacity: viewMode === 'bar' ? 0.85 : 1 },
    colors: SERIES_COLORS,
    xaxis: {
      type: 'datetime',
      // Só impomos min/max quando há um zoom manual a preservar. Sem zoom ativo,
      // omitimos as chaves por completo (em vez de as fixar no intervalo dos dados)
      // para não competirmos com o próprio reset/zoom nativo do gráfico — passar
      // sempre um min/max explícito, mesmo "correto", tende a travar o botão de reset.
      ...(zoomRange ? { min: zoomRange.min, max: zoomRange.max } : {}),
      tooltip: { enabled: false },
      labels: {
        style: { fontWeight: 700, colors: isDarkMode ? '#94a3b8' : '#64748b' },
        datetimeUTC: false,
        datetimeFormatter: { year: 'yyyy', month: "MMM 'yy", day: 'dd MMM', hour: 'HH:mm' },
      },
      axisBorder: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
      axisTicks: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
    },
    yaxis: {
      title: { text: `${(currentDataset?.metadata?.sensorType || 'VALORES').toUpperCase()} (${unit})`, style: { fontWeight: 900, color: '#3b82f6', fontSize: '10px' } },
      labels: {
        style: { fontWeight: 800, colors: [isDarkMode ? '#94a3b8' : '#64748b'] },
        formatter: (val) => (val == null ? '' : Number(val).toLocaleString('pt-PT', { maximumFractionDigits: 1 })),
      },
      forceNiceScale: true,
      decimalsInFloat: 1,
    },
    grid: {
      borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
      padding: { top: 0, right: 12, bottom: 0, left: 8 },
    },
    tooltip: {
      theme: isDarkMode ? 'dark' : 'light',
      shared: true,
      intersect: false,
      x: { format: 'dd MMM yyyy HH:mm' },
      style: { fontSize: '12px' },
      y: { formatter: (val) => (val == null ? '—' : `${val.toFixed(2)} ${unit}`) },
    },
    // Pontos escondidos por defeito (linhas/áreas densas ficam mais limpas); aparecem ao passar o rato.
    markers: {
      size: viewMode === 'scatter' ? 5 : 0,
      strokeWidth: 2,
      hover: { size: viewMode === 'scatter' ? 7 : 5 },
      strokeColors: isDarkMode ? '#0b0f1a' : '#fff',
    },
    legend: { position: 'bottom', horizontalAlign: 'center', fontWeight: 700, offsetY: 4, labels: { useSeriesColors: true }, markers: { strokeWidth: 0 } as any },
    theme: { mode: isDarkMode ? 'dark' : 'light' }
  };

  return (
    <div className={`transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-[3000] bg-white dark:bg-[#0b0f1a] p-8 overflow-y-auto' : 'bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm'}`}>

      <div className="flex flex-col gap-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 flex-1 basis-[240px]">
            <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-blue-600/10 text-blue-600 rounded-xl shrink-0"><Activity size={18} /></div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight leading-tight">Monitorização</h3>
                      {isRealtime && (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shrink-0 ${realtimePaused ? 'bg-slate-400/10 text-slate-500 dark:text-slate-400' : 'bg-red-500/10 text-red-500'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${realtimePaused ? 'bg-slate-400' : 'bg-red-500 animate-pulse'}`} /> {realtimePaused ? 'Em pausa' : 'Ao vivo'}
                        </span>
                      )}
                      {isRealtime && (
                        <button
                          onClick={() => setRealtimePaused(p => !p)}
                          className={`p-1.5 rounded-lg transition-all shrink-0 ${realtimePaused ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'}`}
                          title={realtimePaused ? 'Retomar a receção de dados' : 'Pausar a receção de dados'}
                        >
                          {realtimePaused ? <Play size={13} /> : <Pause size={13} />}
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate" title={label}>{label}</p>
                </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
             <div className="flex items-center bg-slate-50 dark:bg-slate-900/50 rounded-xl p-1 border border-slate-200 dark:border-slate-700">
                {currentDataset?.metadata?.supportedChartTypes?.map((t: string) => {
                  const type = chartTypeMap[t];
                  if (!type) return null;
                  return (
                    <button
                      key={t}
                      onClick={() => setViewMode(type)}
                      className={`p-2 rounded-lg transition-all ${viewMode === type ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                      title={t}
                    >
                      {t === 'Linha' && <LineChart size={17}/>}
                      {t === 'Área' && <AreaChart size={17}/>}
                      {t === 'Barras' && <BarChart2 size={17} className="rotate-90"/>}
                      {t === 'Scatter' && <Activity size={17}/>}
                    </button>
                  );
                })}
                {!currentDataset?.metadata?.supportedChartTypes && (
                  <>
                    <button onClick={() => setViewMode('area')} className={`p-2 rounded-lg transition-all ${viewMode === 'area' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}><AreaChart size={17}/></button>
                    <button onClick={() => setViewMode('line')} className={`p-2 rounded-lg transition-all ${viewMode === 'line' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}><LineChart size={17}/></button>
                  </>
                )}
             </div>

             <button onClick={() => setShowProfile(true)} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900/50 rounded-xl px-3 py-2 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 hover:text-blue-600 transition-all" title="Perfil do sensor">
                <Info size={17}/>
                <span className="text-xs font-medium hidden sm:inline">Perfil</span>
             </button>

             <div className="flex items-center bg-slate-50 dark:bg-slate-900/50 rounded-xl p-1 border border-slate-200 dark:border-slate-700">
                <button onClick={() => setShowEvents(true)} className="p-2 text-slate-400 hover:text-blue-600 transition-all rounded-lg relative" title="Eventos do projeto">
                    <Flag size={17}/>
                    {eventos.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-blue-600 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{eventos.length}</span>}
                </button>
                <button onClick={() => setShowRecordings(true)} className="p-2 text-slate-400 hover:text-blue-600 transition-all rounded-lg relative" title="Gravações deste sensor">
                    <History size={17}/>
                    {sensorRecordings.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-blue-600 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{sensorRecordings.length}</span>}
                </button>
                <button onClick={exportCsv} className="p-2 text-slate-400 hover:text-blue-600 transition-all rounded-lg" title="Exportar CSV do intervalo filtrado"><Download size={17}/></button>
                {zoomRange && (
                  <button onClick={() => setZoomRange(null)} className="p-2 text-blue-500 hover:text-blue-600 transition-all rounded-lg" title="Repor zoom do gráfico"><RotateCcw size={17}/></button>
                )}
                <button onClick={adjustHeight} className="p-2 text-slate-400 hover:text-blue-600 transition-all rounded-lg" title="Altura do gráfico"><ArrowsUpFromLine size={17}/></button>
                <button onClick={toggleFullscreen} className="p-2 text-slate-400 hover:text-blue-600 transition-all rounded-lg" title="Ecrã inteiro">
                    {isFullscreen ? <Minimize2 size={17}/> : <Maximize2 size={17}/>}
                </button>
             </div>

             {datasets.length > 1 && (
                <div className="flex items-center bg-slate-50 dark:bg-slate-900/50 rounded-xl p-1 border border-slate-200 dark:border-slate-700" title={recording ? 'Termina ou cancela a gravação para trocar de sensor' : undefined}>
                    <button onClick={prevDataset} disabled={!!recording} className="p-2 rounded-lg transition-all text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400"><ChevronLeft size={17}/></button>
                    <div className="px-3 flex flex-col items-center justify-center leading-tight">
                        <span className="text-xs font-semibold text-slate-900 dark:text-white">{currentIndex + 1} / {datasets.length}</span>
                        <span className="text-[9px] text-slate-400">sensores</span>
                    </div>
                    <button onClick={nextDataset} disabled={!!recording} className="p-2 rounded-lg transition-all text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400"><ChevronRight size={17}/></button>
                </div>
            )}

            {canDelete && (
                <div className="flex items-center gap-2">
                    {onAppendData && !isRealtime && (
                        <button onClick={() => onAppendData(currentDataset!)} className="flex items-center gap-2 px-3 py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all" title="Adicionar dados a este sensor">
                            <Plus size={17} />
                            <span className="text-xs font-medium hidden sm:inline">Dados</span>
                        </button>
                    )}
                    <button onClick={() => onDeleteDataset(currentDataset!.id)} className="p-2.5 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all border border-red-100 dark:border-red-900/30" title="Apagar sensor">
                        <Trash2 size={17} />
                    </button>
                </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-700">
          {isRealtime ? (
            <div className="flex items-center gap-3">
                <div className="p-2 bg-white dark:bg-slate-800 rounded-xl text-red-500 border border-slate-200 dark:border-slate-700"><Radio size={18} /></div>
                <div className="flex flex-col">
                    <span className="text-xs text-slate-400 mb-0.5">Intervalo de leitura</span>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={realtimePollSec}
                        onChange={(e) => setRealtimePollSec(Math.max(0.1, parseFloat(e.target.value) || 0.5))}
                        className="w-16 bg-transparent border-none text-sm font-semibold text-slate-700 dark:text-slate-200 outline-none focus:ring-0 p-0"
                      />
                      <span className="text-sm text-slate-400">segundos</span>
                    </div>
                </div>
            </div>
          ) : null}

          {isRealtime ? (
            <div className="flex items-center gap-3">
                <div className="p-2 bg-white dark:bg-slate-800 rounded-xl text-blue-600 border border-slate-200 dark:border-slate-700"><History size={18} /></div>
                <div className="flex flex-col">
                    <span className="text-xs text-slate-400 mb-0.5">Histórico (até 31 dias)</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => shiftHistoryDays(-1)} title="Dia anterior" className="p-1 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-600">
                        <ChevronLeft size={16} />
                      </button>
                      <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-transparent border-none text-sm font-medium text-slate-700 dark:text-slate-200 outline-none cursor-pointer focus:ring-0 p-0 w-32" />
                      <span className="text-slate-300 dark:text-slate-600">→</span>
                      <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-transparent border-none text-sm font-medium text-slate-700 dark:text-slate-200 outline-none cursor-pointer focus:ring-0 p-0 w-32" />
                      <button onClick={() => shiftHistoryDays(1)} title="Dia seguinte" className="p-1 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-600">
                        <ChevronRight size={16} />
                      </button>
                    </div>
                </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
                <div className="p-2 bg-white dark:bg-slate-800 rounded-xl text-blue-600 border border-slate-200 dark:border-slate-700"><Calendar size={18} /></div>
                <div className="flex flex-col">
                    <span className="text-xs text-slate-400 mb-0.5">Intervalo de datas</span>
                    <div className="flex items-center gap-2">
                      <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-transparent border-none text-sm font-medium text-slate-700 dark:text-slate-200 outline-none cursor-pointer focus:ring-0 p-0 w-32" />
                      <span className="text-slate-300 dark:text-slate-600">→</span>
                      <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-transparent border-none text-sm font-medium text-slate-700 dark:text-slate-200 outline-none cursor-pointer focus:ring-0 p-0 w-32" />
                    </div>
                </div>
            </div>
          )}

          {(startDate || endDate) && (
            <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 dark:bg-slate-700 text-white rounded-xl hover:bg-blue-600 transition-all text-sm font-medium">
              <FilterX size={15} />
              <span>Limpar filtros</span>
            </button>
          )}

          {isRealtime && canDelete && (
            <>
              <div className="w-px h-9 bg-slate-200 dark:bg-slate-700 hidden sm:block" />
              {recording ? (
                <div className="flex items-center gap-3 pl-1 pr-3 py-1.5 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40">
                  <div className="relative w-9 h-9 shrink-0">
                    <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
                      <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" className="text-red-200 dark:text-red-900/50" stroke="currentColor" />
                      <circle
                        cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" strokeLinecap="round"
                        className="text-red-500 transition-all" stroke="currentColor"
                        strokeDasharray={2 * Math.PI * 15.5}
                        strokeDashoffset={2 * Math.PI * 15.5 * (1 - (recording.totalSec ? recordRemainingSec / recording.totalSec : 0))}
                      />
                    </svg>
                    <Circle size={9} className="absolute inset-0 m-auto text-red-500 fill-red-500 animate-pulse" />
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wide">A gravar</span>
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                      {Math.floor(recordRemainingSec / 60)}:{String(recordRemainingSec % 60).padStart(2, '0')} restantes
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 ml-1">
                    <button onClick={() => finishRecording(true)} disabled={savingRecording} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500 text-white rounded-lg text-xs font-semibold hover:bg-emerald-600 transition-all disabled:opacity-50" title="Parar e guardar agora">
                      <Square size={12} className="fill-current" /> Guardar
                    </button>
                    <button onClick={() => finishRecording(false)} disabled={savingRecording} className="p-2 text-slate-400 hover:text-red-500 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all" title="Interromper e excluir a gravação">
                      <X size={15} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowRecordForm(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl hover:border-red-300 hover:text-red-500 dark:hover:border-red-900/50 transition-all text-sm font-medium"
                >
                  <Circle size={13} className="fill-current text-red-500" />
                  <span>Gravar</span>
                </button>
              )}
            </>
          )}

          {isRealtime && realtimeError && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 text-amber-700 dark:text-amber-400 rounded-xl text-xs font-semibold max-w-xs" title={realtimeError}>
              <AlertTriangle size={13} className="shrink-0" /> <span className="truncate">{realtimeError}</span>
            </span>
          )}

          <div className="ml-auto flex items-center gap-4">
            {hasLimits && totalExceed > 0 && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-600 dark:text-red-400 rounded-xl text-xs font-semibold" title="Leituras fora dos limites definidos no perfil do sensor">
                <AlertTriangle size={13} /> {totalExceed.toLocaleString()} fora dos limites
              </span>
            )}
            {hasLimits && totalExceed === 0 && filteredData.length > 0 && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-500" title="Nenhuma leitura fora dos limites definidos">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Dentro dos limites
              </span>
            )}
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{filteredData.length.toLocaleString()}</span>
              <span className="text-xs text-slate-400">leituras</span>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-wrapper" style={{ height: isFullscreen ? '70vh' : `${chartHeight}px` }}>
        <Chart
          options={options}
          series={series}
          type={viewMode}
          height="100%"
        />
      </div>

      {/* Estatísticas do intervalo filtrado, por série */}
      {filteredData.length > 0 && stats.length > 0 && (
        <div className="mt-5 overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-4 font-semibold">Série</th>
                <th className="py-2 pr-4 font-semibold text-right">Última</th>
                <th className="py-2 pr-4 font-semibold text-right">Mín</th>
                <th className="py-2 pr-4 font-semibold text-right">Máx</th>
                <th className="py-2 pr-4 font-semibold text-right">Média</th>
                {hasLimits && <th className="py-2 font-semibold text-right">Fora dos limites</th>}
              </tr>
            </thead>
            <tbody>
              {stats.map(s => {
                const fmt = (v: number | null) => (v == null ? '—' : `${v.toLocaleString('pt-PT', { maximumFractionDigits: 2 })} ${unit}`);
                return (
                  <tr key={s.col} className="border-t border-slate-100 dark:border-slate-700/50">
                    <td className="py-2 pr-4 font-semibold text-slate-700 dark:text-slate-200">{s.col}</td>
                    <td className="py-2 pr-4 text-right font-medium text-slate-600 dark:text-slate-300">{fmt(s.last)}</td>
                    <td className="py-2 pr-4 text-right text-slate-500 dark:text-slate-400">{fmt(s.min)}</td>
                    <td className="py-2 pr-4 text-right text-slate-500 dark:text-slate-400">{fmt(s.max)}</td>
                    <td className="py-2 pr-4 text-right text-slate-500 dark:text-slate-400">{fmt(s.media)}</td>
                    {hasLimits && (
                      <td className={`py-2 text-right font-semibold ${!s.hasLimit ? 'text-slate-400' : s.exceed > 0 ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-500'}`}>
                        {s.hasLimit ? (s.exceed > 0 ? s.exceed.toLocaleString('pt-PT') : '0') : '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Valores concretos que passam o limiar do respetivo eixo, não só a contagem. */}
      {hasLimits && exceedingReadings.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowExceeding(v => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 transition-colors"
          >
            <AlertTriangle size={13} /> Ver {exceedingReadings.length.toLocaleString('pt-PT')} leitura(s) fora dos limites {showExceeding ? '▲' : '▼'}
          </button>
          {showExceeding && (
            <div className="mt-2 max-h-56 overflow-y-auto custom-scrollbar rounded-xl border border-slate-200 dark:border-slate-700">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
                    <th className="py-1.5 px-3 font-semibold">Data/hora</th>
                    <th className="py-1.5 px-3 font-semibold">Eixo</th>
                    <th className="py-1.5 px-3 font-semibold text-right">Valor</th>
                    <th className="py-1.5 px-3 font-semibold text-right">Limiar excedido</th>
                  </tr>
                </thead>
                <tbody>
                  {[...exceedingReadings].reverse().slice(0, 300).map((r, i) => {
                    const idx = columnKeys.indexOf(r.col);
                    const brokeSuperior = r.limiteSuperior != null && r.value > r.limiteSuperior;
                    return (
                      <tr key={`${r.date}-${r.col}-${i}`} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 px-3 text-slate-500 dark:text-slate-400">{new Date(r.date).toLocaleString('pt-PT')}</td>
                        <td className="py-1.5 px-3">
                          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-200">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: SERIES_COLORS[idx % SERIES_COLORS.length] }} /> {r.col}
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-right font-semibold text-red-500">{r.value.toLocaleString('pt-PT', { maximumFractionDigits: 3 })} {unit}</td>
                        <td className="py-1.5 px-3 text-right text-slate-400">
                          {brokeSuperior ? `máx ${r.limiteSuperior}` : `mín ${r.limiteInferior}`} {unit}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {exceedingReadings.length > 300 && (
                <p className="text-[11px] text-slate-400 px-3 py-1.5 border-t border-slate-100 dark:border-slate-800">
                  A mostrar as 300 mais recentes de {exceedingReadings.length.toLocaleString('pt-PT')}.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de eventos do projeto (campanhas, intervenções) */}
      {showEvents && (
        <div className="fixed inset-0 z-[3200] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowEvents(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-lg w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300 flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-600/10 text-blue-600 rounded-xl"><Flag size={18} /></div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Eventos do projeto</h3>
                  <p className="text-xs text-slate-400">Marcados como linhas verticais em todos os gráficos</p>
                </div>
              </div>
              <button onClick={() => setShowEvents(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X size={20} className="text-slate-400" /></button>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar space-y-3 flex-1">
              {eventos.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Ainda não há eventos registados.</p>
              )}
              {[...eventos].sort((a, b) => a.data.localeCompare(b.data)).map(ev => (
                <div key={ev.id} className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: eventColor(ev.tipo) }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{ev.titulo}</p>
                      <p className="text-xs text-slate-400">
                        {new Date(ev.data).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {' · '}{EVENT_TYPES.find(t => t.key === ev.tipo)?.label || 'Outro'}
                      </p>
                    </div>
                  </div>
                  {canDelete && (
                    <button onClick={() => deleteEvent(ev.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all shrink-0" title="Apagar evento">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {canDelete && projectId && (
              <div className="p-5 border-t border-slate-100 dark:border-slate-800 shrink-0 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <input
                    type="text"
                    value={eventForm.titulo}
                    onChange={(e) => setEventForm({ ...eventForm, titulo: e.target.value })}
                    placeholder="Título do evento"
                    className="flex-1 min-w-[160px] p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-medium text-slate-800 dark:text-white placeholder:font-normal"
                  />
                  <input
                    type="date"
                    value={eventForm.data}
                    onChange={(e) => setEventForm({ ...eventForm, data: e.target.value })}
                    className="p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-medium text-slate-800 dark:text-white"
                  />
                  <select
                    value={eventForm.tipo}
                    onChange={(e) => setEventForm({ ...eventForm, tipo: e.target.value })}
                    className="p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none text-sm font-medium text-slate-800 dark:text-white cursor-pointer"
                  >
                    {EVENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <button
                  onClick={addEvent}
                  disabled={savingEvent || !eventForm.titulo.trim() || !eventForm.data}
                  className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Plus size={15} /> {savingEvent ? 'A adicionar…' : 'Adicionar evento'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de escolha de duração para iniciar uma gravação */}
      {showRecordForm && (() => {
        const presets: { [k in typeof recordUnit]: number[] } = { segundos: [10, 30, 60, 120], minutos: [1, 5, 10, 30], horas: [1, 2, 6, 12] };
        const totalSec = recordUnitToSec(recordDuration, recordUnit);
        const friendly = totalSec < 60
          ? `${totalSec}s`
          : totalSec < 3600
            ? `${Math.round(totalSec / 60)} min${totalSec % 60 ? ` ${totalSec % 60}s` : ''}`
            : `${(totalSec / 3600).toFixed(totalSec % 3600 ? 1 : 0)} h`;

        return (
          <div className="fixed inset-0 z-[3200] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowRecordForm(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-sm w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300">
              <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10 shrink-0 flex items-center justify-center">
                    <span className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
                    <span className="relative w-4 h-4 rounded-full bg-red-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Gravar sensor</h3>
                    <p className="text-xs text-slate-400 truncate max-w-[220px]" title={label}>{label}</p>
                  </div>
                </div>
                <button onClick={() => setShowRecordForm(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0"><X size={20} className="text-slate-400" /></button>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Unidade</label>
                  <div className="grid grid-cols-3 gap-1.5 bg-slate-50 dark:bg-slate-900/50 rounded-xl p-1 border border-slate-200 dark:border-slate-700">
                    {(['segundos', 'minutos', 'horas'] as const).map(u => (
                      <button
                        key={u}
                        onClick={() => setRecordUnit(u)}
                        className={`py-2 rounded-lg text-xs font-semibold capitalize transition-all ${recordUnit === u ? 'bg-red-600 text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800'}`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Duração</label>
                  <div className="flex items-center justify-center gap-3 py-2">
                    <button
                      onClick={() => setRecordDuration(d => Math.max(1, d - 1))}
                      className="w-9 h-9 shrink-0 rounded-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-red-500 hover:border-red-300 dark:hover:border-red-900/50 transition-all font-bold"
                    >−</button>
                    <input
                      type="number"
                      min={1}
                      value={recordDuration}
                      onChange={(e) => setRecordDuration(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 text-center bg-transparent border-none text-3xl font-bold text-slate-900 dark:text-white outline-none focus:ring-0 tabular-nums"
                    />
                    <button
                      onClick={() => setRecordDuration(d => d + 1)}
                      className="w-9 h-9 shrink-0 rounded-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-red-500 hover:border-red-300 dark:hover:border-red-900/50 transition-all font-bold"
                    >+</button>
                  </div>
                  <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                    {presets[recordUnit].map(v => (
                      <button
                        key={v}
                        onClick={() => setRecordDuration(v)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${recordDuration === v ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400' : 'bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                      >
                        {v}{recordUnit === 'segundos' ? 's' : recordUnit === 'minutos' ? 'min' : 'h'}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-slate-400 text-center">
                  Grava durante <strong className="text-slate-600 dark:text-slate-300">{friendly}</strong> e guarda sozinha no fim — ou para e guarda mais cedo, ou cancela sem guardar nada.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => setShowRecordForm(false)} className="px-5 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Cancelar</button>
                <button onClick={startRecording} className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-all active:scale-95">
                  <Circle size={13} className="fill-current" /> Iniciar gravação
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showProfile && currentDataset && (
        <div className="fixed inset-0 z-[3200] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm animate-in fade-in duration-300" onClick={closeProfile}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-3xl w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2.5 bg-blue-600/10 text-blue-600 rounded-xl shrink-0"><Activity size={18} /></div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-blue-600">Perfil do sensor</p>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight truncate">{editingProfile ? 'Editar sensor' : label}</h3>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canDelete && !editingProfile && (
                  <button onClick={openEditProfile} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors" title="Editar sensor"><Pencil size={18} /></button>
                )}
                <button onClick={closeProfile} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X size={20} className="text-slate-400" /></button>
              </div>
            </div>

            <div className="p-6 max-h-[72vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* Coluna esquerda: imagem */}
                <div className="sm:col-span-1">
                  {currentDataset.metadata?.imageUrl ? (
                    <img src={currentDataset.metadata.imageUrl} alt={label} className="w-full aspect-square object-cover rounded-2xl border border-slate-200 dark:border-slate-800" />
                  ) : (
                    <div className="w-full aspect-square rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-300 dark:text-slate-600">
                      <Activity size={40} />
                    </div>
                  )}
                </div>

                {/* Coluna direita: detalhes / formulário */}
                <div className="sm:col-span-2 space-y-4">
                  {editingProfile ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Nome</label>
                        <input type="text" value={profileForm.label} onChange={(e) => setProfileForm({ ...profileForm, label: e.target.value })} className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium text-slate-800 dark:text-white" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">IFC ID</label>
                          <input type="text" value={profileForm.ifcId} onChange={(e) => setProfileForm({ ...profileForm, ifcId: e.target.value })} className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium text-slate-800 dark:text-white" placeholder="GlobalId IFC" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Instalação</label>
                          <input type="date" value={profileForm.dataInstalacao} onChange={(e) => setProfileForm({ ...profileForm, dataInstalacao: e.target.value })} className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium text-slate-800 dark:text-white" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Limites de alerta por eixo</label>
                        <div className="space-y-2">
                          {columnKeys.map((col, idx) => (
                            <div key={col} className="flex items-center gap-2 p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: SERIES_COLORS[idx % SERIES_COLORS.length] }} />
                              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 w-10 shrink-0">{col}</span>
                              <input
                                type="number" step="any" placeholder="Mín"
                                value={profileForm.limites?.[col]?.limiteInferior ?? ''}
                                onChange={(e) => setProfileForm({ ...profileForm, limites: { ...profileForm.limites, [col]: { ...profileForm.limites?.[col], limiteInferior: e.target.value } } })}
                                className="w-full min-w-0 p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-medium text-slate-800 dark:text-white placeholder:font-normal"
                              />
                              <input
                                type="number" step="any" placeholder="Máx"
                                value={profileForm.limites?.[col]?.limiteSuperior ?? ''}
                                onChange={(e) => setProfileForm({ ...profileForm, limites: { ...profileForm.limites, [col]: { ...profileForm.limites?.[col], limiteSuperior: e.target.value } } })}
                                className="w-full min-w-0 p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-medium text-slate-800 dark:text-white placeholder:font-normal"
                              />
                              <span className="text-xs text-slate-400 shrink-0 w-8">{unit}</span>
                            </div>
                          ))}
                          {columnKeys.length === 0 && <p className="text-xs text-slate-400">Sem colunas detetadas neste sensor.</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Descrição</label>
                        <textarea value={profileForm.description} onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })} className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 min-h-[90px] text-slate-700 dark:text-slate-200" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                          <p className="text-xs text-slate-400 mb-0.5">Tipo</p>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">{currentDataset.metadata?.sensorType || '—'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                          <p className="text-xs text-slate-400 mb-0.5">Unidade</p>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">{currentDataset.metadata?.unit || '—'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                          <p className="text-xs text-slate-400 mb-0.5 flex items-center gap-1.5"><Box size={11} /> IFC ID</p>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white break-all">{currentDataset.metadata?.ifcId || '—'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                          <p className="text-xs text-slate-400 mb-0.5 flex items-center gap-1.5"><Calendar size={11} /> Instalação</p>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">{currentDataset.metadata?.dataInstalacao ? new Date(currentDataset.metadata.dataInstalacao).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</p>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800 col-span-2">
                          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1.5"><AlertTriangle size={11} /> Limites de alerta</p>
                          {hasLimits ? (
                            <div className="space-y-1">
                              {columnKeys.filter(col => limites[col]).map(col => {
                                const idx = columnKeys.indexOf(col);
                                const lim = limites[col];
                                return (
                                  <div key={col} className="flex items-center gap-2 text-sm">
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: SERIES_COLORS[idx % SERIES_COLORS.length] }} />
                                    <span className="font-semibold text-slate-700 dark:text-slate-200 w-8 shrink-0">{col}</span>
                                    <span className="text-slate-500 dark:text-slate-400">
                                      {lim?.limiteInferior != null ? `${lim.limiteInferior} ${unit}` : '—'} a {lim?.limiteSuperior != null ? `${lim.limiteSuperior} ${unit}` : '—'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">Sem limites</p>
                          )}
                        </div>
                        {isRealtime && (
                          <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800 col-span-2">
                            <p className="text-xs text-slate-400 mb-0.5 flex items-center gap-1.5"><Radio size={11} /> Fonte (tempo real)</p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white break-all">{currentDataset.metadata?.dataSource || '—'}</p>
                            <p className="text-xs text-slate-400 mt-0.5">Leitura a cada {realtimePollSec}s</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 mb-1">Descrição</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{currentDataset.metadata?.description || 'Sem descrição.'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {!editingProfile && (
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                  {hasIfc && projectId && currentDataset.metadata?.ifcId && (
                    <button
                      onClick={() => setShowObjectViewer(true)}
                      className="inline-flex items-center justify-center gap-2 py-2.5 px-5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all active:scale-95"
                    >
                      <Box size={16} /> <span>Ver objeto IFC 3D</span>
                    </button>
                  )}
                  <div className="flex items-center gap-2 text-xs text-slate-400 sm:ml-auto">
                    <Hash size={12} /> {filteredData.length.toLocaleString()} leituras
                  </div>
                </div>
              )}
            </div>

            {editingProfile && (
              <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => setEditingProfile(false)} className="px-5 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Cancelar</button>
                <button onClick={saveProfile} disabled={savingProfile} className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-900 dark:bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-black dark:hover:bg-blue-700 transition-all disabled:opacity-60">
                  {savingProfile ? 'A guardar…' : 'Guardar'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showObjectViewer && projectId && currentDataset?.metadata?.ifcId && (
        <IfcViewerModal
          isOpen={showObjectViewer}
          onClose={() => setShowObjectViewer(false)}
          projectId={projectId}
          fileName={label}
          highlightGuid={currentDataset.metadata.ifcId}
        />
      )}

      {showRecordings && projectId && (
        <RecordingsModal
          isOpen={showRecordings}
          onClose={() => setShowRecordings(false)}
          projectId={projectId}
          recordings={sensorRecordings}
          sensorLabel={label}
          canDelete={canDelete}
          onChange={onDatasetUpdated}
        />
      )}

      <style>{`
        .chart-wrapper .apexcharts-canvas { outline: none !important; }
        .chart-wrapper .apexcharts-tooltip { border: none !important; box-shadow: none !important; }
      `}</style>
    </div>
  );
};

export default MonitoringDashboard;
